import "./index.scss";
import * as _ from "lodash";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useEffect } from "react";
import {
  editProfile,
  getListProfile,
  getListTimeZone,
  removeProfile,
} from "../../../store/profile/thunkAction";
import Dropdown from "../../../components/core/dropdown";
import Table from "../../../components/core/table";
import Popup from "../../../components/core/popup";
import { profileAction } from "../../../store/profile/slice";
import { useTranslation } from "react-i18next";
import { ScheduleType } from "../../../constant/enums";
import { getImageUrl } from "../../../utils/image";
import {
  sendNotifyIziMessageError,
  sendNotifyIziMessageSuccess,
} from "../../../utils/event";
import { syncProfileSetting } from "../../../store/profile-setting/thunkAction";

const ProfileManage = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState(null);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });

  const profileManage = useSelector((state) => state.profileManage);
  const tokenManage = useSelector((state) => state.tokenManage);

  const profileSettingManage = useSelector(
    (state) => state.profileSettingManage,
  );
  const profileSetting = _.get(profileSettingManage, "profileSetting");

  const isActiveSetting = _.get(profileSetting, "isActive", true);
  const currentNoSetting = _.get(profileSetting, "currentNo", 0);
  const quantitySetting = _.get(profileSetting, "quantity", 0);
  const allowedSetting = currentNoSetting <= quantitySetting && isActiveSetting;

  const hasToken = _.get(tokenManage, "hasToken");
  const profiles = _.get(profileManage, "profiles");
  const profile = _.get(profileManage, "profile");
  const timeZones = _.get(profileManage, "timeZones");
  const profileId = _.get(profile, "_id");
  const isActive = _.get(profile, "isActive", false);
  const isChatBot = _.get(profile, "isChatBot", false);

  useEffect(() => {
    if (!isActiveSetting) {
      sendNotifyIziMessageError(t("profile.profile_setting_not_active"));
    }
  }, [isActiveSetting, t]);

  useEffect(() => {
    if (currentNoSetting > quantitySetting) {
      sendNotifyIziMessageError(t("profile.profile_limit"));
    }
  }, [currentNoSetting, quantitySetting, t]);

  useEffect(() => {
    if (hasToken) {
      const filter = {
        search,
        status,
      };

      if (!search) {
        dispatch(getListProfile(filter));

        return;
      }

      const searchTimeout = setTimeout(() => {
        dispatch(getListProfile(filter));
      }, 1000);

      return () => clearTimeout(searchTimeout);
    }
  }, [dispatch, search, status, hasToken]);

  useEffect(() => {
    if (hasToken) {
      dispatch(getListTimeZone());
      dispatch(syncProfileSetting());
    }
  }, [hasToken, dispatch]);

  const optionsStatus = [
    { name: t("common.active"), id: true },
    { name: t("common.inactive"), id: false },
  ];

  const closePopup = () => {
    setIsOpen(false);
  };

  const handleSelectStatus = (option) => {
    setStatus(_.get(option, "id", null));
  };

  const onChangeActive = async (checked) => {
    const filter = { search, status };
    const canChangeActive = checked ? currentNoSetting < quantitySetting : true;

    try {
      if (!canChangeActive) {
        sendNotifyIziMessageError(t("profile.cant_active_profile"));
        return;
      }

      await dispatch(
        editProfile({ id: profileId, data: { isActive: checked } }),
      ).unwrap();

      dispatch(getListProfile(filter));
    } catch (error) {
      sendNotifyIziMessageError(t("profile.profile_save_error"));
    } finally {
      dispatch(syncProfileSetting());
    }
  };

  const onChangeChatBot = async (checked) => {
    const filter = {
      search,
      status,
    };
    await dispatch(
      editProfile({ id: profileId, data: { isChatBot: checked } }),
    ).unwrap();
    dispatch(getListProfile(filter));
  };

  const configTable = {
    header: [
      {
        key: "no",
        label: "NO.",
        className: "text-center small",
        render: (row, rowIndex) => rowIndex + 1,
      },
      {
        key: "domain",
        label: t("settings.manage.profile.domain"),
        className: "small",
        render: (row) => {
          const imageUrl = _.get(row, "imageUrl");
          const domain = _.get(row, "domain");
          const profileId = _.get(row, "_id");

          return allowedSetting ? (
            <a
              aria-current="page"
              className="color-dark d-flex gap-2 align-items-center active"
              href={`#/profile/${profileId}/detail`}
              data-discover="true"
            >
              <img width="24px" alt="" src={getImageUrl(imageUrl)} />
              <div>{domain}</div>
            </a>
          ) : (
            <div
              className="color-dark d-flex gap-2 align-items-center disabled"
              data-discover="true"
            >
              <img width="24px" alt="" src={getImageUrl(imageUrl)} />
              <div>{domain}</div>
            </div>
          );
        },
      },
      {
        key: "schedule",
        label: t("settings.manage.profile.schedule"),
        className: "text-center small",
        render: (row) => {
          const schedule = _.get(row, "schedule", ScheduleType.ENTIRE_DAY);

          return schedule === ScheduleType.ENTIRE_DAY
            ? t("settings.manage.profile.schedule_type.entire_day")
            : t("settings.manage.profile.schedule_type.business_hours");
        },
      },
      {
        key: "chatBot",
        label: t("settings.manage.profile.chat_bot"),
        className: "text-center small",
        render: (row) => {
          const isChatBot = _.get(row, "isChatBot", false);

          return isChatBot ? (
            <img alt="" src="/img/chat/checked.png" />
          ) : (
            <img alt="" src="/img/chat/uncheck.png" />
          );
        },
      },
      {
        key: "liveChat",
        label: t("settings.manage.profile.live_chat"),
        className: "text-center small",
        render: (row) => {
          const isLiveChat = _.get(row, "isLiveChat", false);

          return isLiveChat ? (
            <img alt="" src="/img/chat/checked.png" />
          ) : (
            <img alt="" src="/img/chat/uncheck.png" />
          );
        },
      },
      {
        key: "chatBadge",
        label: t("settings.manage.profile.chat_badge"),
        className: "text-center small",
        render: (row) => {
          const isChatBadge = _.get(
            row,
            "profileAppearance.chatBadge.enabled",
            false,
          );

          return isChatBadge ? (
            <img alt="" src="/img/chat/checked.png" />
          ) : (
            <img alt="" src="/img/chat/uncheck.png" />
          );
        },
      },
      {
        key: "language",
        label: t("settings.manage.profile.language"),
        className: "text-center small",
        render: (row) => {
          const language = _.get(row, "language", "en");

          return t(`common.language.${language}`);
        },
      },
      {
        key: "timeZone",
        label: "TIMEZONE",
        className: "text-center small",
        render: (row) => {
          const value = _.get(row, "profileAppearance.content.timeZone", null);
          const timeZone = _.find(
            timeZones,
            (timeZone) => timeZone.value === value,
          );

          return _.get(timeZone, "text", "");
        },
      },
      {
        key: "status",
        label: t("settings.manage.profile.status"),
        className: "text-center small",
        render: (row) => {
          const isActive = _.get(row, "isActive", false);

          return (
            <div className="d-flex gap-4" style={{ justifyContent: "center" }}>
              <div className="w-125px">
                {isActive ? (
                  <span className="btn btn-success-light btn-sm">
                    {t("common.active")}
                  </span>
                ) : (
                  <span className="btn btn-danger-light btn-sm">
                    {t("common.inactive")}
                  </span>
                )}
              </div>
            </div>
          );
        },
      },
      {
        key: "manage",
        label: t("settings.manage.manage_title"),
        className: "text-center small",
        render: (row, index) => {
          return (
            <div className="d-flex gap-4" style={{ justifyContent: "center" }}>
              <button
                type="button"
                className="icon-button"
                onClick={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  setPopupPosition({
                    top:
                      index < 6
                        ? rect.bottom + window.scrollY
                        : rect.bottom - 400 + window.scrollY,
                    left: rect.left - 300 + window.scrollX,
                  });

                  setIsOpen(true);
                  dispatch(profileAction.setProfile(row));
                }}
              >
                <i className="fa fa-cog"></i>
              </button>
            </div>
          );
        },
      },
    ],
    collection: profiles,
  };

  return (
    <div
      className="tab-pane fade active show"
      id="profiles-tab-pane"
      role="tabpanel"
      aria-labelledby="profiles-tab"
      tabIndex="0"
    >
      <div className="row mb-3 g-3">
        <div className="col-lg-4">
          <div className="input-icon">
            <i className="fa fa-search"></i>
            <input
              type="text"
              className="form-control has-icon search"
              placeholder={t("settings.manage.profile.search")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
        <div className="col-lg-2 d-flex align-items-center">
          <Dropdown
            config={{
              options: optionsStatus,
              onSelect: handleSelectStatus,
              placeholder: t("settings.manage.profile.status"),
              value: status,
              className: "button-status",
            }}
          />
        </div>
        <div className="col-lg-3"></div>
        {allowedSetting && (
          <div className="col-lg-2 offset-lg-1">
            <a
              href="#/profile/add"
              type="submit"
              className="btn btn-primary w-100"
            >
              {t("common.add")}
            </a>
          </div>
        )}
      </div>
      <div className="table-responsive table-box mb-5">
        <Table config={configTable} />
      </div>
      <Popup
        config={{
          isOpen: isOpen,
          onClose: closePopup,
          position: popupPosition,
          className: "option-popup",
        }}
      >
        <div className="modal-content">
          <ul className="list-group rounded-4 overflow-hidden py-3">
            <li className="list-group-item list-group-item-action border-0 rounded-0">
              <div className="d-flex justify-content-between align-items-center">
                <span>{t("settings.manage.profile.activate_profile")}</span>
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="flexSwitchCheckChecked"
                    checked={isActive}
                    onChange={(e) => onChangeActive(e.target.checked)}
                  />
                </div>
              </div>
            </li>
            {/* {allowedSetting && (
              <li className="list-group-item list-group-item-action border-0 rounded-0">
                <div className="d-flex justify-content-between align-items-center">
                  <span>{t("settings.manage.profile.enable_chat_bot")}</span>
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="flexSwitchCheckChecked"
                      checked={isChatBot}
                      onChange={(e) => onChangeChatBot(e.target.checked)}
                    />
                  </div>
                </div>
              </li>
            )} */}
            {allowedSetting && (
              <li>
                <hr className="my-3" />
              </li>
            )}
            {allowedSetting && (
              <li className="list-group-item list-group-item-action border-0 rounded-0">
                <a
                  href={`#/profile/${profileId}/bot-setting`}
                  className="color-dark d-flex"
                >
                  <span>{t("settings.manage.profile.bot_setting")}</span>
                </a>
              </li>
            )}
            {allowedSetting && (
              <li className="list-group-item list-group-item-action border-0 rounded-0">
                <a
                  href={`#/profile/${profileId}/detail`}
                  className="color-dark d-flex"
                >
                  <span>{t("settings.manage.profile.general")}</span>
                </a>
              </li>
            )}
            {allowedSetting && (
              <li className="list-group-item list-group-item-action border-0 rounded-0">
                <a
                  href={`#/profile/${profileId}/appearances`}
                  className="color-dark d-flex"
                >
                  <span>{t("settings.manage.profile.appearance")}</span>
                </a>
              </li>
            )}
            {allowedSetting && (
              <li className="list-group-item list-group-item-action border-0 rounded-0">
                <a
                  href={`#/profile/${profileId}/form`}
                  className="color-dark d-flex"
                >
                  <span>{t("settings.manage.profile.form")}</span>
                </a>
              </li>
            )}
            {allowedSetting && (
              <li className="list-group-item list-group-item-action border-0 rounded-0">
                <a
                  href={`#/profile/${profileId}/shorthand`}
                  className="color-dark d-flex"
                >
                  <span>{t("settings.manage.profile.shorthand")}</span>
                </a>
              </li>
            )}
            {allowedSetting && (
              <li className="list-group-item list-group-item-action border-0 rounded-0">
                <a
                  href={`#/profile/${profileId}/trigger`}
                  className="color-dark d-flex"
                >
                  <span>{t("settings.manage.profile.trigger")}</span>
                </a>
              </li>
            )}
            {allowedSetting && (
              <li className="list-group-item list-group-item-action border-0 rounded-0">
                <a
                  href={`#/profile/${profileId}/routing`}
                  className="color-dark d-flex"
                >
                  <span>{t("settings.manage.profile.routing")}</span>
                </a>
              </li>
            )}
            <li
              onClick={async () => {
                closePopup();
                try {
                  await dispatch(removeProfile(profileId)).unwrap();
                  dispatch(syncProfileSetting());
                  sendNotifyIziMessageSuccess(
                    t("profile.profile_remove_success"),
                  );
                } catch (error) {
                  sendNotifyIziMessageError(t("profile.profile_remove_error"));
                }
                const filter = {
                  search,
                  status,
                };

                dispatch(getListProfile(filter));
              }}
              className="list-group-item list-group-item-action border-0 rounded-0"
            >
              <button
                style={{
                  background: "none",
                  border: "none",
                  color: "red",
                  textAlign: "left",
                  paddingBlock: "0px",
                  paddingInline: "0px",
                }}
              >
                {t("common.delete")}
              </button>
            </li>
          </ul>
        </div>
      </Popup>
    </div>
  );
};

export default ProfileManage;
